import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Folder, CheckCircle2, ListTodo } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { ProjectCard, ProjectForm } from '../components/projects';
import { TaskCard } from '../components/tasks/TaskCard';
import { useProjectStore } from '../stores/projectStore';
import { useTaskStore } from '../stores/taskStore';
import { useUIStore } from '../stores/uiStore';
import { modalScrollLock } from '../utils/modalScrollLock';

export const ProjectsPage: React.FC = () => {
  const { projects, isLoading, fetchProjects } = useProjectStore();
  const { tasks, fetchTasks, isLoading: tasksLoading, clearTasks } = useTaskStore();
  const { openProjectModal, projectModalOpen } = useUIStore();
  const { t } = useTranslation();

  // Scroll lock — ProjectForm is always mounted but shows Modal conditionally via projectModalOpen
  useEffect(() => {
    if (projectModalOpen) {
      modalScrollLock.register();
    }
    return () => {
      if (projectModalOpen) {
        modalScrollLock.unregister();
      }
    };
  }, [projectModalOpen]);
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Fetch tasks when project selection changes
  useEffect(() => {
    if (selectedProjectIds.size > 0) {
      // Fetch tasks for all selected projects
      const projectIdsArray = Array.from(selectedProjectIds);
      fetchTasks({ projectIds: projectIdsArray.join(',') });
    } else {
      // Clear tasks when no projects are selected
      clearTasks();
    }
  }, [selectedProjectIds, fetchTasks, clearTasks]);

  // Toggle project selection
  const toggleProjectSelection = (projectId: string) => {
    setSelectedProjectIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  // Clear all project filters
  const clearAllFilters = () => {
    setSelectedProjectIds(new Set());
  };

  // Remove single project from filter
  const removeProjectFilter = (projectId: string) => {
    setSelectedProjectIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(projectId);
      return newSet;
    });
  };

  // Get selected projects for display
  const selectedProjects = useMemo(() => {
    return projects.filter(p => selectedProjectIds.has(p.id));
  }, [projects, selectedProjectIds]);

  // Separate tasks into pending and completed
  const { pendingTasks, completedTasks } = useMemo(() => {
    const pending = tasks.filter(t => !t.completed);
    const completed = tasks.filter(t => t.completed);
    return { pendingTasks: pending, completedTasks: completed };
  }, [tasks]);

  return (
    <div className="flex flex-col h-full">
      <Header title={t('projects.title')} />
      
      <main className="flex-1 overflow-y-auto p-4 sm:p-6">
        {/* Selected Project Filters */}
        {selectedProjectIds.size > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {t('projects.filteringBy')}
            </span>
            {selectedProjects.map(project => (
              <button
                key={project.id}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 min-h-[44px] rounded-full text-sm font-medium transition-opacity hover:opacity-80"
                style={{ backgroundColor: `${project.color}20`, color: project.color }}
                onClick={() => removeProjectFilter(project.id)}
              >
                <Folder className="w-3.5 h-3.5" />
                <span>{project.name}</span>
                <X className="w-3.5 h-3.5 ml-1" />
              </button>
            ))}
            <button
              onClick={clearAllFilters}
              className="text-sm min-h-[44px] inline-flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline"
            >
              {t('projects.clearAll')}
            </button>
          </div>
        )}

        {isLoading && projects.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">{t('projects.loading')}</div>
          </div>
        ) : projects.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <ListTodo className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4" />
            <div className="text-gray-900 dark:text-gray-100 font-medium mb-2">
              {t('projects.emptyTitle')}
            </div>
            <p className="text-sm text-gray-500 mb-4">
              {t('projects.emptySubtitle')}
            </p>
            <button
              onClick={() => openProjectModal()}
              className="px-4 py-2 min-h-[44px] bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              {t('projects.createProject')}
            </button>
          </div>
        ) : (
          <>
            {/* Projects Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.map((project) => (
                <ProjectCard 
                  key={project.id} 
                  project={project} 
                  isSelected={selectedProjectIds.has(project.id)}
                  onToggleSelect={toggleProjectSelection}
                />
              ))}
            </div>

            {/* Tasks Section - Show when projects are selected */}
            {selectedProjectIds.size > 0 && (
              <div className="mt-8">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  {t('projects.tasks', { count: tasks.length })}
                </h2>
                
                {tasksLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="text-gray-500">{t('projects.loadingTasks')}</div>
                  </div>
                ) : tasks.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <ListTodo className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4" />
                    <div className="text-gray-900 dark:text-gray-100 font-medium mb-2">
                      {t('projects.noTasksInSelected')}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                      {t('projects.noTasksSubtitle')}
                    </p>
                    <button
                      onClick={clearAllFilters}
                      className="px-4 py-2 min-h-[44px] text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 underline transition-colors"
                    >
                      {t('projects.clearAllFilters')}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Pending Tasks */}
                    {pendingTasks.length > 0 && (
                      <div className="space-y-3">
                        {pendingTasks.map((task) => (
                          <TaskCard key={task.id} task={task} />
                        ))}
                      </div>
                    )}

                    {/* Completed Tasks */}
                    {completedTasks.length > 0 && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                          <CheckCircle2 className="w-4 h-4" />
                          <span>{t('projects.completedCount', { count: completedTasks.length })}</span>
                        </div>
                        {completedTasks.map((task) => (
                          <TaskCard key={task.id} task={task} />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>

      <ProjectForm />
    </div>
  );
};

export default ProjectsPage;